import React, { useState, useEffect } from 'react';
import { X, GripVertical, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { WIDGETS } from './widgets';

// Modal that lets the operator pick which widgets show on their dashboard
// and reorder them via drag. Returns the new ordered id list to the parent
// on Save (the parent is responsible for persisting + applying it).
const CustomizeDashboardModal = ({ isOpen, onClose, onSave, currentLayout, allowedWidgetIds, defaultLayout }) => {
  // `selected` keeps the active set in display order; `available` is
  // everything else, alphabetised.
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    const filtered = (currentLayout || []).filter((id) => allowedWidgetIds.includes(id));
    setSelected(filtered.length > 0 ? filtered : defaultLayout);
  }, [isOpen, currentLayout, allowedWidgetIds, defaultLayout]);

  if (!isOpen) return null;

  const available = allowedWidgetIds.filter((id) => !selected.includes(id));

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const next = Array.from(selected);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setSelected(next);
  };

  const toggle = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((x) => x !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Customize dashboard</h2>
            <p className="text-xs text-gray-500 mt-0.5">Toggle widgets and drag to reorder. Saved per user.</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Visible / ordered widgets */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Visible on dashboard ({selected.length})
              </h3>
              <button
                onClick={() => setSelected(defaultLayout)}
                className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                title="Restore the default set for your role"
              >
                <RotateCcw size={11} /> Reset to default
              </button>
            </div>
            {selected.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg">
                No widgets selected. Pick from below.
              </div>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="selected">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1.5">
                      {selected.map((id, idx) => {
                        const w = WIDGETS[id];
                        if (!w) return null;
                        const Icon = w.icon;
                        return (
                          <Draggable key={id} draggableId={id} index={idx}>
                            {(prov, snap) => (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                className={`flex items-center gap-2 p-2.5 bg-white border rounded-md ${snap.isDragging ? 'shadow-lg border-primary-300' : 'border-gray-200'}`}
                              >
                                <span {...prov.dragHandleProps} className="cursor-grab active:cursor-grabbing text-gray-400">
                                  <GripVertical size={14} />
                                </span>
                                <Icon size={14} className="text-primary-600" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900">{w.title}</div>
                                  <div className="text-[11px] text-gray-500 truncate">{w.description}</div>
                                </div>
                                <span className="text-[10px] uppercase tracking-wider text-gray-400">{w.size}</span>
                                <button
                                  onClick={() => toggle(id)}
                                  className="ml-2 p-1.5 text-gray-400 hover:text-red-600"
                                  title="Hide"
                                >
                                  <EyeOff size={14} />
                                </button>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </section>

          {/* Available (hidden) widgets */}
          {available.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Available widgets ({available.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {available.map((id) => {
                  const w = WIDGETS[id];
                  if (!w) return null;
                  const Icon = w.icon;
                  return (
                    <button
                      key={id}
                      onClick={() => toggle(id)}
                      className="flex items-start gap-2 p-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md text-left"
                    >
                      <Icon size={14} className="text-gray-500 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">{w.title}</div>
                        <div className="text-[11px] text-gray-500">{w.description}</div>
                      </div>
                      <Eye size={14} className="text-primary-600 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={() => { onSave(selected); onClose(); }}
            className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium"
          >
            Save layout
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomizeDashboardModal;
